import React, { useCallback } from 'react';
import { Button, Input, Space, TableProps, message, Tooltip } from 'antd';
import CommonTable from 'components/CommonTable';
import type { ColumnsType } from 'antd/es/table';
import s from './s.module.less';
import { SearchOutlined } from '@ant-design/icons';
import { getTextVisitCaseList } from 'api/operation/textVisitCase';
import type { TTextVisitCaseItem, TTextVisitCaseListParams } from 'types/textVisitCase';
import { ETextVisitRefundStatus } from 'types/textVisitCase';
import { TextVisitServiceType } from 'types/textVisit';
import { getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import FilterPanel, { FilterGroup } from '../FilterPanel';
import dayjs from 'dayjs';
import TextVisitSubmittedDrawer from 'components/TextVisitSubmittedDrawer';
import {
    CASE_STATUS_OPTIONS,
    CASE_STATUS_DISPLAY_MAP,
    PAYMENT_STATUS_OPTIONS,
    PAYMENT_STATUS_DISPLAY_MAP,
    REFUND_STATUS_OPTIONS,
    REFUND_STATUS_DISPLAY_MAP,
} from 'constants/textVisit';
import AccessStore from 'store/Access/permission';
import { PermissionModuleKeys } from 'constants/access';

type Props = {
    assignedProviderId?: number;
    from?: 'providerCase';
};

const CasesTable = (props: Props) => {
    const { assignedProviderId, from } = props;
    const isFromProviderCase = from === 'providerCase' && !!assignedProviderId;
    // Get access permissions
    const [getAccessStore] = AccessStore.useStore();
    const access = getAccessStore('data');
    // State management
    const [itemList, setItemList] = React.useState<TTextVisitCaseItem[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const pageSize = 10;

    // Filter states
    const [pendingPatientNameFilter, setPendingPatientNameFilter] = React.useState('');
    const [pendingProviderFilter, setPendingProviderFilter] = React.useState('');
    const [pendingConditionFilter, setPendingConditionFilter] = React.useState('');
    const [pendingWorkflowNameFilter, setPendingWorkflowNameFilter] = React.useState('');
    const [patientNameFilter, setPatientNameFilter] = React.useState<string>('');
    const [caseIdFilter, setCaseIdFilter] = React.useState<string>('');
    const [providerFilter, setProviderFilter] = React.useState<string>('');
    const [conditionFilter, setConditionFilter] = React.useState<string>('');
    const [stateSearchFilter, setStateSearchFilter] = React.useState<string>('');
    const [workflowNameFilter, setWorkflowNameFilter] = React.useState<string>('');

    // Sort states
    const [sortBy, setSortBy] = React.useState<string>('');
    const [sortType, setSortType] = React.useState<string>('');

    // Filter panel states
    const [caseStatusFilters, setCaseStatusFilters] = React.useState<string[]>([]);
    const [paymentStatusFilters, setPaymentStatusFilters] = React.useState<string[]>([]);
    const [refundStatusFilters, setRefundStatusFilters] = React.useState<string[]>([]);
    const [refundReasonFilters, setRefundReasonFilters] = React.useState<string[]>([]);
    const [treatmentPlanFilters, setTreatmentPlanFilters] = React.useState<string[]>([]);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [selectedCase, setSelectedCase] = React.useState<TTextVisitCaseItem | null>(null);
    // Filter panel collapse state
    const [isFilterPanelCollapsed, setIsFilterPanelCollapsed] = React.useState(false);

    // 使用 useSettings 获取州列表数据
    const [settings] = useSettings();
    const stateOptions = React.useMemo(() => settings[EnumFields.STATE] || [], [settings]);

    // Data fetching function
    const doFetch = useCallback(
        async (targetPage: number = page) => {
            setLoading(true);
            // 立即清空列表，避免显示过时数据
            setItemList([]);
            setTotal(0);

            try {
                // 处理州搜索：将完整州名称转换为对应的 value
                const getStateValue = (searchTerm: string): string => {
                    if (!searchTerm) return '';
                    // 首先尝试直接匹配 value
                    const directMatch = stateOptions.find((option) => option.value === searchTerm);
                    if (directMatch) return searchTerm;
                    // 然后尝试匹配 label
                    const labelMatch = stateOptions.find(
                        (option) => option.label && option.label.toLowerCase().includes(searchTerm.toLowerCase()),
                    );
                    return labelMatch ? String(labelMatch.value) : searchTerm;
                };

                // 处理Visit Notes筛选器：将SUBMITTED/NOT_SUBMITTED转换为true/false
                const getTreatmentSubmittedValue = (): boolean | undefined => {
                    if (treatmentPlanFilters.length === 0) return undefined;
                    if (treatmentPlanFilters.includes('SUBMITTED') && !treatmentPlanFilters.includes('NOT_SUBMITTED')) {
                        return true;
                    }
                    if (treatmentPlanFilters.includes('NOT_SUBMITTED') && !treatmentPlanFilters.includes('SUBMITTED')) {
                        return false;
                    }
                    // 如果两个都选中或都没选中，返回undefined（不过滤）
                    return undefined;
                };

                // 构建API请求参数
                const params: TTextVisitCaseListParams = {
                    patientName: patientNameFilter || '',
                    caseId: caseIdFilter || '',
                    assignProviderName: providerFilter || '',
                    status: caseStatusFilters.length > 0 ? caseStatusFilters : undefined,
                    paymentStatus: paymentStatusFilters.length > 0 ? paymentStatusFilters : undefined,
                    refundStatus:
                        refundStatusFilters.length > 0 ? (refundStatusFilters as ETextVisitRefundStatus[]) : undefined,
                    refundReasonType: refundReasonFilters.length > 0 ? refundReasonFilters : undefined,
                    treatmentSubmitted: getTreatmentSubmittedValue(),
                    condition: conditionFilter || '',
                    state: getStateValue(stateSearchFilter || '') || undefined,
                    assessmentName: workflowNameFilter || undefined,
                    sortBy: sortBy || undefined,
                    sortType: sortType || undefined,
                    size: pageSize,
                    current: targetPage,
                };
                if (isFromProviderCase) {
                    params.assignedProviderId = assignedProviderId;
                }

                // 调用API
                const { data, error } = await getTextVisitCaseList(params);

                // 确保数据正确返回
                if (error) {
                    // 有错误，显示错误信息
                    message.error(error || 'Failed to load data. Please try again.');
                } else if (data && data.data) {
                    // 成功返回数据
                    setItemList(data.data.list || []);
                    setTotal(data.data.totalRecords || 0);
                }
            } catch (error) {
                message.error('Failed to fetch case data.');
            } finally {
                setLoading(false);
            }
        },
        [
            patientNameFilter,
            caseIdFilter,
            providerFilter,
            conditionFilter,
            stateSearchFilter,
            workflowNameFilter,
            caseStatusFilters,
            paymentStatusFilters,
            refundStatusFilters,
            refundReasonFilters,
            treatmentPlanFilters,
            sortBy,
            sortType,
            stateOptions,
            page,
            pageSize,
            isFromProviderCase,
            assignedProviderId,
        ],
    );

    // Table change handler
    const onTableChange: TableProps<TTextVisitCaseItem>['onChange'] = (pagination, filters, sorter) => {
        // Handle sorting first
        if (sorter && !Array.isArray(sorter) && sorter.field) {
            const { field, order } = sorter;

            // field 对应 dataIndex
            if (field === 'patientSubmitAt' || field === 'submittedAt') {
                setSortBy('SUBMITTED_AT');
                setSortType(order === 'ascend' ? 'ASC' : 'DESC');
                setPage(1); // Reset to first page when sorting changes
            } else if (field === 'updatedAt' || field === 'lastModified') {
                setSortBy('LAST_MODIFIED');
                setSortType(order === 'ascend' ? 'ASC' : 'DESC');
                setPage(1); // Reset to first page when sorting changes
            }
        } else {
            const pageInx = pagination?.current || 1;
            setPage(pageInx);
        }
    };

    // Handle view case
    const handleViewCase = (record: TTextVisitCaseItem) => {
        setIsModalOpen(true);
        setSelectedCase(record);
    };

    // Clear all filters function
    const handleClearAllFilters = () => {
        setCaseStatusFilters([]);
        setPaymentStatusFilters([]);
        setRefundStatusFilters([]);
        setRefundReasonFilters([]);
        setTreatmentPlanFilters([]);
        setPatientNameFilter('');
        setCaseIdFilter('');
        setProviderFilter('');
        setConditionFilter('');
        setStateSearchFilter('');
        setWorkflowNameFilter('');
        setPendingPatientNameFilter('');
        setPendingProviderFilter('');
        setPendingConditionFilter('');
        setPendingWorkflowNameFilter('');
        setSortBy('');
        setSortType('');
        setPage(1);
    };

    // Toggle filter panel collapse
    const handleToggleFilterPanelCollapse = () => {
        setIsFilterPanelCollapsed((prev) => !prev);
    };

    // Handler functions for filter changes that reset page to 1
    const handleCaseStatusFiltersChange = React.useCallback((values: string[]) => {
        setCaseStatusFilters(values);
        setPage(1);
    }, []);

    const handlePaymentStatusFiltersChange = React.useCallback((values: string[]) => {
        setPaymentStatusFilters(values);
        setPage(1);
    }, []);

    const handleRefundStatusFiltersChange = React.useCallback((values: string[]) => {
        setRefundStatusFilters(values);
        setPage(1);
    }, []);

    const handleRefundReasonFiltersChange = React.useCallback((values: string[]) => {
        setRefundReasonFilters(values);
        setPage(1);
    }, []);

    const handleTreatmentPlanFiltersChange = React.useCallback((values: string[]) => {
        setTreatmentPlanFilters(values);
        setPage(1);
    }, []);

    // Check if there are any active filters
    const hasActiveFilters = React.useMemo(() => {
        return (
            caseStatusFilters.length > 0 ||
            paymentStatusFilters.length > 0 ||
            refundStatusFilters.length > 0 ||
            refundReasonFilters.length > 0 ||
            treatmentPlanFilters.length > 0 ||
            patientNameFilter !== '' ||
            caseIdFilter !== '' ||
            providerFilter !== '' ||
            conditionFilter !== '' ||
            stateSearchFilter !== '' ||
            workflowNameFilter !== '' ||
            sortBy !== '' ||
            sortType !== ''
        );
    }, [
        caseStatusFilters,
        paymentStatusFilters,
        refundStatusFilters,
        refundReasonFilters,
        treatmentPlanFilters,
        patientNameFilter,
        caseIdFilter,
        providerFilter,
        conditionFilter,
        stateSearchFilter,
        workflowNameFilter,
        sortBy,
        sortType,
    ]);

    // Filter groups configuration
    const filterGroups: FilterGroup[] = React.useMemo(
        () => [
            {
                title: 'Case status',
                options: CASE_STATUS_OPTIONS,
                selectedValues: caseStatusFilters,
                onSelectionChange: handleCaseStatusFiltersChange,
            },
            {
                title: 'Payment status',
                options: PAYMENT_STATUS_OPTIONS,
                selectedValues: paymentStatusFilters,
                onSelectionChange: handlePaymentStatusFiltersChange,
            },
            {
                title: 'Refund status',
                options: REFUND_STATUS_OPTIONS,
                selectedValues: refundStatusFilters,
                onSelectionChange: handleRefundStatusFiltersChange,
            },
            {
                title: 'Visit Notes',
                options: [
                    { label: 'Submitted', value: 'SUBMITTED' },
                    { label: 'Not submitted', value: 'NOT_SUBMITTED' },
                ],
                selectedValues: treatmentPlanFilters,
                onSelectionChange: handleTreatmentPlanFiltersChange,
            },
            {
                title: 'Refund reason',
                options: [
                    {
                        label: 'No visit notes received',
                        value: 'No visit notes received',
                    },
                    {
                        label: 'Provider unable to treat my case',
                        value: 'Provider unable to treat my case',
                    },
                    {
                        label: "Don't need this visit anymore",
                        value: "Don't need this visit anymore",
                    },
                    { label: 'Booked by mistake', value: 'Booked by mistake' },
                    { label: 'Other', value: 'Other' },
                ],
                selectedValues: refundReasonFilters,
                onSelectionChange: handleRefundReasonFiltersChange,
            },
        ],
        [
            caseStatusFilters,
            paymentStatusFilters,
            refundStatusFilters,
            refundReasonFilters,
            treatmentPlanFilters,
            handleCaseStatusFiltersChange,
            handlePaymentStatusFiltersChange,
            handleRefundStatusFiltersChange,
            handleRefundReasonFiltersChange,
            handleTreatmentPlanFiltersChange,
        ],
    );

    // Track previous values to detect changes
    const prevPageRef = React.useRef(page);
    const prevFiltersRef = React.useRef<string>(
        JSON.stringify({
            caseStatusFilters,
            paymentStatusFilters,
            refundStatusFilters,
            refundReasonFilters,
            treatmentPlanFilters,
            patientNameFilter,
            caseIdFilter,
            providerFilter,
            conditionFilter,
            stateSearchFilter,
            workflowNameFilter,
            sortBy,
            sortType,
        }),
    );

    // Fetch data when page or filters change
    React.useEffect(() => {
        const currentFilters = JSON.stringify({
            caseStatusFilters,
            paymentStatusFilters,
            refundStatusFilters,
            refundReasonFilters,
            treatmentPlanFilters,
            patientNameFilter,
            caseIdFilter,
            providerFilter,
            conditionFilter,
            stateSearchFilter,
            workflowNameFilter,
            sortBy,
            sortType,
        });

        // Always fetch data when filters or page change
        doFetch(page);
        prevFiltersRef.current = currentFilters;
        prevPageRef.current = page;
    }, [
        caseStatusFilters,
        paymentStatusFilters,
        refundStatusFilters,
        refundReasonFilters,
        treatmentPlanFilters,
        patientNameFilter,
        caseIdFilter,
        providerFilter,
        conditionFilter,
        stateSearchFilter,
        workflowNameFilter,
        sortBy,
        sortType,
        page,
        doFetch,
    ]);

    const columns: ColumnsType<TTextVisitCaseItem> = [
        {
            title: 'PATIENT NAME',
            dataIndex: 'patientFirstName',
            key: 'patientName',
            width: '246px',
            filterDropdown: () => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search patient name"
                        value={pendingPatientNameFilter}
                        onChange={(e) => {
                            setPendingPatientNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setPatientNameFilter(pendingPatientNameFilter);
                            setPage(1);
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setPatientNameFilter(pendingPatientNameFilter);
                                setPage(1);
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingPatientNameFilter('');
                                setPatientNameFilter('');
                                setPage(1);
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            render: (text: string, record: TTextVisitCaseItem) => (
                <div className={s.patientCell}>
                    <div className={s.patientName}>{`${record.patientFirstName} ${record.patientLastName}`.trim()}</div>
                    <div className={s.patientEmail}>{record.patientEmail}</div>
                </div>
            ),
        },
        {
            title: 'WORKFLOW NAME',
            dataIndex: 'assessmentName',
            key: 'workflowName',
            width: '200px',
            filterDropdown: () => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search workflow name"
                        value={pendingWorkflowNameFilter}
                        onChange={(e) => {
                            setPendingWorkflowNameFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setWorkflowNameFilter(pendingWorkflowNameFilter);
                            setPage(1);
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setWorkflowNameFilter(pendingWorkflowNameFilter);
                                setPage(1);
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingWorkflowNameFilter('');
                                setWorkflowNameFilter('');
                                setPage(1);
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            render: (text: string, record: TTextVisitCaseItem) => {
                const isRefill =
                    record.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
                    record.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;
                const displayText = isRefill
                    ? getRefillServiceTitle({
                          serviceType: record.serviceType,
                          specialty: record.specialty ?? null,
                          assessmentName: text ?? null,
                      }) || text
                    : text;
                return <span className={s.caseId}>{displayText || '-'}</span>;
            },
        },
        isFromProviderCase
            ? false
            : {
                  title: 'ASSIGNED PROVIDER',
                  dataIndex: 'assignedProviderFirstName',
                  key: 'assignedProvider',
                  width: '196px',
                  filterDropdown: () => (
                      <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                          <Input
                              placeholder="Search provider"
                              value={pendingProviderFilter}
                              onChange={(e) => {
                                  setPendingProviderFilter(e.target.value);
                              }}
                              onPressEnter={() => {
                                  setProviderFilter(pendingProviderFilter);
                                  setPage(1);
                              }}
                              style={{ marginBottom: 8, display: 'block' }}
                          />
                          <Space>
                              <Button
                                  type="primary"
                                  onClick={() => {
                                      setProviderFilter(pendingProviderFilter);
                                      setPage(1);
                                  }}
                                  icon={<SearchOutlined />}
                                  size="small"
                                  style={{ width: 90 }}
                              >
                                  Search
                              </Button>
                              <Button
                                  onClick={() => {
                                      setPendingProviderFilter('');
                                      setProviderFilter('');
                                      setPage(1);
                                  }}
                                  size="small"
                                  style={{ width: 90 }}
                              >
                                  Reset
                              </Button>
                          </Space>
                      </div>
                  ),
                  filterIcon: (filtered: boolean) => (
                      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
                  ),
                  render: (firstName: string, record: TTextVisitCaseItem) => (
                      <span className={s.providerName}>
                          {record.assignedProviderFirstName && record.assignedProviderLastName
                              ? `${record.assignedProviderFirstName} ${record.assignedProviderLastName}`.trim()
                              : '-'}
                      </span>
                  ),
              },
        {
            title: 'CASE STATUS',
            dataIndex: 'status',
            key: 'caseStatus',
            width: '165px',
            // filters: [
            //     { text: 'Unassigned', value: 'UNASSIGNED' },
            //     { text: 'Active', value: 'ACTIVE' },
            //     { text: 'Closed', value: 'CLOSED' },
            // ],
            render: (status: string) => {
                const displayStatus = CASE_STATUS_DISPLAY_MAP[status] || status || '';
                const cssClass = displayStatus ? displayStatus.toLowerCase().replace(/\s+/g, '') : '';
                return (
                    <span className={`${s.statusBadge} ${cssClass ? s[cssClass] : ''}`}>{displayStatus || '-'}</span>
                );
            },
        },
        {
            title: 'PAYMENT STATUS',
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            width: '165px',
            // filters: [
            //     { text: 'Paid', value: 'SUCCEEDED' },
            //     { text: 'Failed', value: 'FAILED' },
            //     { text: 'Init', value: 'INIT' },
            //     { text: 'Processing', value: 'PROCESSING' },
            //     { text: 'Pending', value: 'PENDING' },
            //     { text: 'Refunded', value: 'REFUNDED' },
            //     { text: 'Refund Processing', value: 'REFUNDED_PROCESSING' },
            //     { text: 'Canceled', value: 'CANCELED' },
            //     { text: 'Disputed', value: 'DISPUTED' },
            // ],
            render: (status: string, record: TTextVisitCaseItem) => {
                const displayStatus = PAYMENT_STATUS_DISPLAY_MAP[status] || status || '';
                const cssClass = displayStatus ? displayStatus.toLowerCase().replace(/\s+/g, '') : '';

                // 如果是退款相关状态，添加tooltip
                if (status === 'REFUNDED' || status === 'REFUNDED_FAILED' || status === 'REFUNDED_PROCESSING') {
                    return (
                        <Tooltip
                            overlayClassName={s.refundedTooltip}
                            overlayInnerStyle={{
                                padding: '12px',
                                borderRadius: '8px',
                                maxWidth: '300px',
                            }}
                            color="#FFFFFF"
                            title={
                                <div className={s.tooltipContent}>
                                    <p className={s.tooltipMessage}>
                                        Refunded: <span>${record.refundAmount}</span>
                                    </p>
                                    <p className={s.tooltipMessage}>
                                        Refund reason: <span>{record.refundReason || '-'}</span>
                                    </p>
                                    <p className={s.tooltipMessage}>
                                        Notes from Klarity team: <span>{record.notesInternal || '-'}</span>
                                    </p>
                                </div>
                            }
                            placement="top"
                        >
                            <div className={`${s.statusBadge} ${cssClass ? s[cssClass] : ''}`}>
                                {displayStatus || '-'}
                                <div className={s.tips} />
                            </div>
                        </Tooltip>
                    );
                }

                return (
                    <span className={`${s.statusBadge} ${cssClass ? s[cssClass] : ''}`}>{displayStatus || '-'}</span>
                );
            },
        },
        {
            title: 'REFUND STATUS',
            dataIndex: 'refundStatus',
            key: 'refundStatus',
            width: '165px',
            // filters: [
            //     { text: 'Not Applicable', value: null },
            //     { text: 'Pending review', value: 'PENDING' },
            //     { text: 'Denied', value: 'DENIED' },
            //     { text: 'Approved (Partial)', value: 'APPROVE_PARTIAL' },
            //     { text: 'Approved (Full)', value: 'APPROVE_FULL' },
            // ],
            render: (status: ETextVisitRefundStatus | string | null) => {
                if (!status) {
                    return <span className={`${s.statusBadge} ${s.notapplicable}`}>Not Applicable</span>;
                }

                // Create status to CSS class mapping
                const statusMap: Record<string, string> = {
                    [ETextVisitRefundStatus.PENDING_REVIEW]: 'pendingreview',
                    [ETextVisitRefundStatus.APPROVE_FULL]: 'approvefull',
                    [ETextVisitRefundStatus.APPROVE_PARTIAL]: 'approvepartial',
                    [ETextVisitRefundStatus.DENIED]: 'denied',
                    [ETextVisitRefundStatus.AUTO_APPROVED]: 'autoapproved',
                };

                const cssClass = statusMap[status] || '';
                const displayText = REFUND_STATUS_DISPLAY_MAP[status] || status;

                return <span className={`${s.statusBadge} ${s[cssClass]}`}>{displayText}</span>;
            },
        },
        {
            title: 'TREATMENT PLAN',
            dataIndex: 'providerTreatmentSubmitted',
            key: 'treatmentPlan',
            width: '212px',
            render: (submitted: boolean) => (
                <span className={s.treatmentPlan}>{submitted ? 'Submitted' : 'Not submitted'}</span>
            ),
        },
        {
            title: 'STATES',
            dataIndex: 'state',
            key: 'states',
            width: '140px',
            render: (state: string) => {
                // 从 stateOptions 中查找对应的州名称
                const stateObj = stateOptions.find((stateOption) => stateOption.value === state);
                const stateLabel = stateObj?.label || state;
                return <span className={s.stateText}>{stateLabel}</span>;
            },
        },
        {
            title: 'CONDITION',
            dataIndex: 'conditions',
            key: 'condition',
            width: '140px',
            filterDropdown: () => (
                <div style={{ padding: 16 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        placeholder="Search condition"
                        value={pendingConditionFilter}
                        onChange={(e) => {
                            setPendingConditionFilter(e.target.value);
                        }}
                        onPressEnter={() => {
                            setConditionFilter(pendingConditionFilter);
                            setPage(1);
                        }}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => {
                                setConditionFilter(pendingConditionFilter);
                                setPage(1);
                            }}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => {
                                setPendingConditionFilter('');
                                setConditionFilter('');
                                setPage(1);
                            }}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            render: (condition: string) => <span className={s.conditionText}>{condition}</span>,
        },
        {
            title: 'SUBMITTED AT',
            dataIndex: 'patientSubmitAt',
            key: 'submittedAt',
            sortDirections: ['ascend', 'descend', 'ascend'],
            width: '140px',
            sorter: true,
            render: (time: string) => {
                return <div>{time ? dayjs(time).format('MM/DD/YYYY') : '-'}</div>;
            },
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'updatedAt',
            key: 'lastModified',
            width: '160px',
            sortDirections: ['ascend', 'descend', 'ascend'],
            sorter: true,
            render: (time: string) => {
                return <div>{time ? dayjs(time).format('MM/DD/YYYY') : '-'}</div>;
            },
        },
        {
            title: '',
            width: '230px',
            fixed: 'right',
            render: (record: TTextVisitCaseItem) => {
                // Check if should show "Review refund request" based on status and permission
                const isRefundPendingReview = record.refundStatus === ETextVisitRefundStatus.PENDING_REVIEW;
                const hasRefundPermission = access?.[PermissionModuleKeys.TEXT_VISIT_CASE_REFUND];
                const shouldShowRefundText = isRefundPendingReview && hasRefundPermission;

                return (
                    <div className={s.actionWrap}>
                        <span onClick={() => handleViewCase(record)} className={s.link}>
                            {shouldShowRefundText ? 'Review refund request' : 'View cases'}
                        </span>
                    </div>
                );
            },
        },
    ].filter(Boolean) as ColumnsType<TTextVisitCaseItem>;

    // Check if current case is the last pending review item
    const isLastPendingReview = React.useMemo(() => {
        if (!selectedCase) return false;
        const currentIndex = itemList.findIndex((it) => it.textVisitUuid === selectedCase?.textVisitUuid);
        // Check if there's any pending review case after current one
        for (let i = currentIndex + 1; i < itemList.length; i++) {
            if (itemList[i].refundStatus === ETextVisitRefundStatus.PENDING_REVIEW) {
                return false;
            }
        }
        return true;
    }, [itemList, selectedCase]);

    return (
        <div className={s.wrap}>
            {!false && (
                <FilterPanel
                    filterGroups={filterGroups}
                    onClearAll={handleClearAllFilters}
                    hasActiveFilters={hasActiveFilters}
                    isCollapsed={isFilterPanelCollapsed}
                    onToggleCollapse={handleToggleFilterPanelCollapse}
                />
            )}
            <CommonTable
                bordered
                rowKey="textVisitUuid"
                pagination={{
                    total,
                    current: page,
                    pageSize,
                    showSizeChanger: false,
                }}
                scroll={{ x: 'max-content' }}
                loading={loading}
                columns={columns}
                onChange={onTableChange}
                data={itemList || []}
                locale={{
                    emptyText: 'No cases yet',
                }}
            />
            <TextVisitSubmittedDrawer
                showChatMessage
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                textVisitUuid={selectedCase?.textVisitUuid}
                refundRequestId={selectedCase?.refundRequestId}
                refundStatus={selectedCase?.refundStatus}
                isOps
                isLastPendingReview={isLastPendingReview}
                hasRefundPermission={access?.[PermissionModuleKeys.TEXT_VISIT_CASE_REFUND]}
                onRefundSubmitted={async (action) => {
                    // refresh list first
                    await doFetch(page);
                    if (action === 'home') {
                        setIsModalOpen(false);
                        setSelectedCase(null);
                        return;
                    }

                    // find next pending review case from current list
                    const currentIndex = itemList.findIndex((it) => it.textVisitUuid === selectedCase?.textVisitUuid);
                    let next: TTextVisitCaseItem | undefined;
                    for (let i = Math.max(0, currentIndex + 1); i < itemList.length; i++) {
                        if (itemList[i].refundStatus === ETextVisitRefundStatus.PENDING_REVIEW) {
                            next = itemList[i];
                            break;
                        }
                    }
                    if (next) {
                        setSelectedCase(next);
                        setIsModalOpen(true);
                    } else {
                        message.info('No more pending review refund requests');
                        setIsModalOpen(false);
                        setSelectedCase(null);
                    }
                }}
            />
        </div>
    );
};

export default CasesTable;
